/**
 * Autosave loop — v0M15.5.
 *
 * Fires on the interval configured in Settings → Autosave, writing the
 * current SavedGame to the reserved `autosave` slot. Subscribes to
 * settings changes so the interval re-configures when the user picks
 * a new frequency (or turns it off).
 *
 * Design (v0M15-design):
 *   - The loop takes a `getSavedGame` callback rather than the sim
 *     driver directly, so the caller (main.ts) can construct the
 *     SavedGame from whatever state is current at fire time. That
 *     also decouples autosave from any specific driver shape.
 *   - Autosave writes are silent — no toast / UI feedback. Rationale:
 *     Michael's autosave setting is opt-in (Off is a valid value); if
 *     the user enabled it, they don't want a notification every 5
 *     minutes. First playtest signal will decide if this needs a
 *     brief HUD flash.
 *   - Errors from writeSlot (quota exceeded, corrupt state) are logged
 *     but do NOT propagate. Autosave failing mid-session shouldn't
 *     crash the game.
 *   - Slot name embeds the game day so the picker shows meaningful
 *     progression. Format: `Autosave — Day 42`.
 *
 * Consumed by main.ts's startGame path; torn down on return-to-main /
 * exit (implicit — full page reload destroys the loop with the tab
 * state).
 */

import { writeSlot } from './save-storage';
import { autosaveFrequencyMs, loadSettings, subscribeSettings } from './settings-storage';
import type { SavedGame } from '../sim/save';

/** MECHANICS.md §1.1 — MFix3.8 raised this from 5 to 25 ticks/day. */
const TICKS_PER_GAME_DAY = 25;

export interface AutosaveLoopHandle {
  /** Tear down the loop + the settings-storage subscription. Idempotent. */
  destroy(): void;
  /**
   * Trigger an autosave now, off-schedule. Used at natural checkpoints
   * (e.g. victory) so the player never has to wait through a full
   * interval to preserve the state they just reached. No-op if
   * autosave is Off.
   */
  saveNow(): void;
}

/**
 * Start the autosave loop. Returns a handle; caller is responsible for
 * calling `destroy()` on teardown (though in practice we tear down via
 * page reload, which drops the interval automatically).
 */
export function startAutosaveLoop(getSavedGame: () => SavedGame): AutosaveLoopHandle {
  let intervalHandle: ReturnType<typeof setInterval> | null = null;

  /** (Re)configure the interval from the current settings. */
  function configureInterval(): void {
    if (intervalHandle !== null) {
      clearInterval(intervalHandle);
      intervalHandle = null;
    }
    const ms = autosaveFrequencyMs(loadSettings().autosave.frequency);
    if (ms === null) return; // Off — no interval.
    intervalHandle = setInterval(() => {
      writeAutosave(getSavedGame);
    }, ms);
  }

  configureInterval();

  // Re-configure when the frequency changes. Volume-only settings
  // changes will also fire this — cheap.
  const unsubscribe = subscribeSettings(() => configureInterval());

  let destroyed = false;
  return {
    destroy(): void {
      if (destroyed) return;
      destroyed = true;
      if (intervalHandle !== null) {
        clearInterval(intervalHandle);
        intervalHandle = null;
      }
      unsubscribe();
    },
    saveNow(): void {
      if (destroyed) return;
      if (autosaveFrequencyMs(loadSettings().autosave.frequency) === null) return;
      writeAutosave(getSavedGame);
    },
  };
}

function writeAutosave(getSavedGame: () => SavedGame): void {
  try {
    const saved = getSavedGame();
    const day = Math.floor(saved.state.tick / TICKS_PER_GAME_DAY);
    writeSlot('autosave', saved, `Autosave — Day ${day}`);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn(`[SU] autosave failed: ${err instanceof Error ? err.message : String(err)}`);
  }
}
